import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/axios';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Plus, FileText, Clock, CheckCircle, Settings, LogOut, Moon, Sun, Users, BarChart3, Search, Globe, Shield } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme, theme } = useTheme();
  const { logout, user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('total'); // 'total', 'in_progress', 'submitted', 'completed'

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const response = await api.get('/assessments');
      setAssessments(response.data);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssessment = () => {
    navigate('/assessments/new');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400';
      case 'approved': return 'text-green-600 dark:text-green-400';
      case 'reviewed': return 'text-blue-600 dark:text-blue-400';
      case 'submitted': return 'text-yellow-600 dark:text-yellow-400';
      case 'in_progress': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5" />;
      case 'approved': return <CheckCircle className="w-5 h-5" />;
      case 'reviewed': return <FileText className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  // Filter by status tab
  const statusFilteredAssessments = assessments.filter(assessment => {
    if (statusFilter === 'total') return true;
    if (statusFilter === 'in_progress') return assessment.assessment_status === 'in_progress' || assessment.assessment_status === 'created';
    if (statusFilter === 'submitted') return assessment.assessment_status === 'submitted' || assessment.assessment_status === 'reviewed';
    if (statusFilter === 'completed') return assessment.assessment_status === 'approved' || assessment.assessment_status === 'completed';
    return true;
  });

  // Filter by search term
  const filteredAssessments = statusFilteredAssessments.filter(assessment => {
    const searchLower = searchTerm.toLowerCase();
    const clientName = assessment.client?.full_name || '';
    const clientEmail = assessment.client?.email || '';
    const assessmentDate = new Date(assessment.created_at).toLocaleDateString();
    const assessmentStatus = assessment.assessment_status || '';

    return (
      clientName.toLowerCase().includes(searchLower) ||
      clientEmail.toLowerCase().includes(searchLower) ||
      assessmentDate.includes(searchLower) ||
      assessmentStatus.toLowerCase().includes(searchLower)
    );
  });

  const statCards = [
    { key: 'total', label: 'Total', count: assessments.length, icon: BarChart3, color: theme.primary, bgColor: `${theme.primary}20` },
    { key: 'in_progress', label: 'In Progress', count: assessments.filter(a => a.assessment_status === 'in_progress').length, icon: Clock, color: '#f59e0b', bgColor: '#f59e0b20' },
    { key: 'submitted', label: 'Submitted', count: assessments.filter(a => a.assessment_status === 'submitted' || a.assessment_status === 'reviewed').length, icon: FileText, color: '#3b82f6', bgColor: '#3b82f620' },
    { key: 'completed', label: 'Completed', count: assessments.filter(a => a.assessment_status === 'approved' || a.assessment_status === 'completed').length, icon: CheckCircle, color: '#10b981', bgColor: '#10b98120' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      {/* Header */}
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>RDA Investment Dashboard</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                Investment Assessment Dashboard
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                {isDark ? <Sun className="w-5 h-5" style={{ color: theme.text }} /> : <Moon className="w-5 h-5" />}
              </button>
              <Button variant="ghost" onClick={() => navigate('/security')}>
                <Shield className="w-4 h-4 mr-2" /> Security
              </Button>
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {statCards.map(({ key, label, count, icon: Icon, color, bgColor }) => (
            <Card
              key={key}
              className="cursor-pointer transition-all"
              style={statusFilter === key ? { outline: `2px solid ${color}`, outlineOffset: '-2px' } : {}}
              onClick={() => setStatusFilter(key)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>{label}</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: theme.text }}>{count}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: bgColor }}>
                    <Icon className="w-6 h-6" style={{ color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-4 mb-8">
          <Button onClick={handleCreateAssessment} size="lg">
            <Plus className="w-5 h-5 mr-2" /> New Assessment
          </Button>
          {user?.role === 'admin' && (
            <Button variant="secondary" onClick={() => navigate('/admin')}>
              <Settings className="w-5 h-5 mr-2" /> Admin
            </Button>
          )}
        </div>

        {/* Assessments List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold" style={{ color: theme.text }}>Recent Assessments</h2>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: theme.textSecondary }} />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.surface,
                    border: `1px solid ${theme.border}`,
                    color: theme.text
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" style={{ color: theme.primary }}></div>
              </div>
            ) : filteredAssessments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: theme.text }} />
                <p className="text-lg" style={{ color: theme.textSecondary }}>
                  {searchTerm ? 'No assessments match your search' : 'No assessments yet'}
                </p>
                <p className="text-sm mt-2" style={{ color: theme.textSecondary }}>
                  {searchTerm ? 'Try a different search term' : 'Create your first assessment to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAssessments.map((assessment) => (
                  <div
                    key={assessment.id}
                    className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors hover:opacity-80"
                    style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
                    onClick={() => navigate(`/assessments/${assessment.id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={getStatusColor(assessment.assessment_status)}>
                        {getStatusIcon(assessment.assessment_status)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium" style={{ color: theme.text }}>
                            {assessment.client?.full_name}
                          </p>
                          {assessment.source === 'public' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                              <Globe className="w-3 h-3" /> Internet review
                            </span>
                          )}
                        </div>
                        <p className="text-sm" style={{ color: theme.textSecondary }}>
                          {new Date(assessment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(assessment.assessment_status)}`}>
                        {assessment.assessment_status === 'approved' ? 'completed' : assessment.assessment_status.replace('_', ' ')}
                      </span>
                      <span className="text-2xl" style={{ color: theme.primary }}>→</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
